import { documentKeys } from '@/hooks/use-documents'
import { jobPreferencesKeys } from '@/hooks/use-job-preferences'
import { learningKeys } from '@/hooks/use-learning'
import { authenticatedFetch } from '@/lib/authenticated-fetch'
import { useAuthStore } from '@/stores/auth.store'
import type { JobPreferences } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 30000
/**
 * Number of consecutive failed connection attempts to tolerate silently before
 * logging an error. The first attempt on page mount can race the auth/fetch
 * lifecycle and fail with a transient "TypeError: network error" that the
 * exponential-backoff reconnect heals automatically (PEL-435). We only surface
 * an error once retries have genuinely failed.
 */
const ERROR_LOG_THRESHOLD = 3

type EventType =
  | 'connected'
  | 'snapshot'
  | 'heartbeat'
  // Document events
  | 'document_created'
  | 'document_status_changed'
  | 'document_processing_progress'
  | 'document_deleted'
  | 'job_matching_status_changed'
  | 'job_matching_progress'
  | 'job_search_created'
  | 'job_search_status_changed'
  | 'job_search_progress'
  | 'job_apply_resume_status_changed'
  | 'revision_created'
  | 'revision_status_changed'
  // Learning events
  | 'learning_profile_status_changed'
  | 'learning_topic_created'
  | 'learning_topic_generating'
  | 'learning_topic_status_changed'
  | 'learning_topic_podcast_status_changed'
  | 'learning_module_created'
  | 'learning_module_audio_status_changed'
  | 'job_preferences_changed'
  | 'job_preferences_deleted'
  | 'hris_sync_completed'

interface SSEEvent {
  type: EventType
  data?: {
    job_preferences?: JobPreferences | null
    document_id?: number
    job_search_id?: number
    job_id?: number
    apply_run_id?: number
    status?: string
    provider?: string
  }
}

type EventListener = (event: SSEEvent) => void

/** Global listeners registry so multiple hooks can subscribe */
const listeners = new Set<EventListener>()

/** Singleton connection state */
let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null
let activeController: AbortController | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let isConnecting = false
let currentToken: string | null = null
let consecutiveFailures = 0

function notifyListeners(event: SSEEvent) {
  for (const listener of listeners) {
    try {
      listener(event)
    } catch (e) {
      console.error('SSE listener error:', e)
    }
  }
}

async function connect(token: string) {
  if (isConnecting || activeReader || activeController) return
  isConnecting = true
  currentToken = token
  const controller = new AbortController()
  activeController = controller

  try {
    const response = await authenticatedFetch(`${API_URL}/api/v1/events/stream`, {
      headers: {
        Accept: 'text/event-stream',
      },
      signal: controller.signal,
    })

    // StrictMode can unmount/remount while fetch is still pending. Never let
    // that stale response install a second reader after disconnect().
    if (controller.signal.aborted || activeController !== controller) {
      await response.body?.cancel()
      return
    }

    if (response.status === 403) {
      // Authorization is definitive. Keep the signed-in session but stop this
      // resource stream instead of creating a forbidden reconnect loop.
      disconnect()
      return
    }

    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No reader available')

    activeReader = reader
    isConnecting = false
    reconnectDelay = RECONNECT_DELAY
    consecutiveFailures = 0

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event: SSEEvent = JSON.parse(line.slice(6))
            notifyListeners(event)
          } catch {
            // Skip malformed events
          }
        }
      }
    }
  } catch (e) {
    if (controller.signal.aborted || activeController !== controller) return
    consecutiveFailures += 1
    // Suppress the noisy, self-healing error on the initial mount race and
    // only log once the backoff reconnect has repeatedly failed (PEL-435).
    if (consecutiveFailures >= ERROR_LOG_THRESHOLD) {
      console.error(`SSE connection error (attempt ${consecutiveFailures}):`, e)
    }
  } finally {
    // A newer connection owns the singleton state; this stale attempt must not
    // clear it or schedule an additional reconnect.
    if (activeController === controller) {
      activeController = null
      activeReader = null
      isConnecting = false
      scheduleReconnect()
    }
  }
}

let reconnectDelay = RECONNECT_DELAY

function scheduleReconnect() {
  if (reconnectTimer) return
  if (!currentToken) return
  if (listeners.size === 0) return

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (currentToken && listeners.size > 0) {
      connect(currentToken)
    }
  }, reconnectDelay)

  // Exponential backoff, capped
  reconnectDelay = Math.min(reconnectDelay * 1.5, MAX_RECONNECT_DELAY)
}

function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (activeController) {
    const controller = activeController
    activeController = null
    controller.abort()
  }
  if (activeReader) {
    activeReader.cancel().catch(() => {
      // Ignore cancel errors during disconnect
    })
    activeReader = null
  }
  currentToken = null
  isConnecting = false
  reconnectDelay = RECONNECT_DELAY
  consecutiveFailures = 0
}

function subscribe(listener: EventListener): () => void {
  listeners.add(listener)

  // Start connection if this is the first listener and we have a token
  if (listeners.size === 1 && currentToken) {
    connect(currentToken)
  }

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      disconnect()
    }
  }
}

/**
 * Main hook: connects to the SSE event stream when authenticated.
 * Automatically invalidates TanStack Query caches on status changes.
 * Should be mounted once in the app layout.
 */
export function useEventStream() {
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isAuthenticated || !token) {
      disconnect()
      return
    }

    currentToken = token
    reconnectDelay = RECONNECT_DELAY
    consecutiveFailures = 0

    const unsubscribe = subscribe((event) => {
      switch (event.type) {
        case 'document_created':
        case 'document_deleted':
          queryClient.invalidateQueries({ queryKey: documentKeys.list() })
          queryClient.invalidateQueries({ queryKey: documentKeys.all })
          queryClient.invalidateQueries({ queryKey: ['job-searches'] })
          queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
          break
        case 'document_status_changed':
          queryClient.invalidateQueries({ queryKey: documentKeys.list() })
          queryClient.invalidateQueries({ queryKey: documentKeys.all })
          if (event.data?.status === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
          }
          break
        case 'job_matching_status_changed':
          queryClient.invalidateQueries({ queryKey: documentKeys.list() })
          queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
          queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
          break
        case 'job_matching_progress':
          queryClient.invalidateQueries({ queryKey: documentKeys.list() })
          queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
          break
        case 'job_search_created':
          queryClient.invalidateQueries({ queryKey: ['job-searches'] })
          break
        case 'job_search_status_changed':
        case 'job_search_progress': {
          queryClient.invalidateQueries({ queryKey: ['job-searches'] })
          const jobSearchId = event.data?.job_search_id
          if (jobSearchId) {
            queryClient.invalidateQueries({ queryKey: ['job-searches', 'detail', jobSearchId] })
          }
          break
        }
        case 'job_apply_resume_status_changed': {
          const jobId = event.data?.job_id
          queryClient.invalidateQueries({ queryKey: ['external-jobs', 'matches'] })
          if (jobId) {
            // Prefix (no jobId) so both the per-job query and the batched
            // apply-resume status query for the Opportunities cards refresh.
            queryClient.invalidateQueries({ queryKey: ['external-jobs', 'apply-resume'] })
          }
          break
        }
        case 'revision_created':
        case 'revision_status_changed':
          queryClient.invalidateQueries({ queryKey: documentKeys.all })
          break
        case 'learning_profile_status_changed':
          queryClient.invalidateQueries({ queryKey: learningKeys.profile() })
          queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          break
        case 'learning_topic_created':
        case 'learning_topic_generating':
        case 'learning_topic_status_changed':
          queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          break
        case 'learning_topic_podcast_status_changed':
          queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          break
        case 'learning_module_created':
        case 'learning_module_audio_status_changed':
          queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
          queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          break
        case 'job_preferences_changed':
        case 'job_preferences_deleted':
          queryClient.invalidateQueries({ queryKey: jobPreferencesKeys.all })
          break
        case 'snapshot':
          if (event.data?.job_preferences !== undefined) {
            queryClient.setQueryData(jobPreferencesKeys.all, event.data.job_preferences ?? null)
          }
          break
      }
    })

    // Start connection
    if (!activeReader && !isConnecting) {
      connect(token)
    }

    return unsubscribe
  }, [isAuthenticated, token, queryClient])
}

/**
 * Hook to subscribe to specific SSE event types.
 * Returns nothing - just calls the callback when matching events arrive.
 */
export function useSSEEvent(
  eventTypes: EventType | EventType[],
  callback: (event: SSEEvent) => void,
) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes]

  // biome-ignore lint/correctness/useExhaustiveDependencies: types array is derived from eventTypes prop
  useEffect(() => {
    const typeSet = new Set(types)
    const unsubscribe = subscribe((event) => {
      if (typeSet.has(event.type)) {
        callbackRef.current(event)
      }
    })
    return unsubscribe
  }, [types.join(',')])
}

export { subscribe, disconnect }
